import React, { useEffect, useMemo, useState } from "react";
import type { ProgressStats } from "@nelurio/contracts";
import { Card, EmptyState } from "@nelurio/ui";
import { useI18n } from "@nelurio/i18n";
import { api } from "../api/client";
import { barHeightPercent, buildBuckets, computeTrend } from "../lib/focusChart";

interface CourseShareItem { courseId: string; title: string; color: string; minutes: number }

type Range = "week" | "month" | "year";

const COURSE_COLORS = ["#6c4cf6", "#4d8df7", "#4dbd74", "#f4b942", "#ff6863", "#34c3b9"];

/** Deterministic color per course id so charts stay stable across renders. */
function courseColor(id: string, index: number) {
  let hash = 0;
  for (let i = 0; i < id.length; i += 1) hash = (hash * 31 + id.charCodeAt(i)) & 0xffffff;
  return COURSE_COLORS[hash % COURSE_COLORS.length] ?? COURSE_COLORS[index % COURSE_COLORS.length];
}

export function FocusCharts({ stats }: { stats: ProgressStats | null }) {
  const { t, formatNumber, locale } = useI18n();
  const [range, setRange] = useState<Range>("week");

  const weeks = stats?.focus.weeks ?? [];
  const totalHours = stats ? Math.round((stats.focus.totalSeconds / 3600) * 10) / 10 : 0;
  const avgMinutes = stats?.focus.sessions ? Math.round(stats.focus.totalSeconds / stats.focus.sessions / 60) : 0;

  // Range aggregation + trend math live in lib/focusChart (unit-tested).
  const buckets = useMemo(() => buildBuckets(weeks, range, locale), [weeks, range, locale]);
  const trend = useMemo(() => computeTrend(buckets), [buckets]);
  const bucketMax = Math.max(1, ...buckets.map((bucket) => bucket.seconds));

  // Where did my time actually go? Completed focus minutes per course.
  const [share, setShare] = useState<{ items: CourseShareItem[]; totalMinutes: number } | null>(null);
  useEffect(() => {
    const days = range === "week" ? 6 : range === "month" ? 29 : 364;
    const to = new Date();
    const from = new Date(to.getFullYear(), to.getMonth(), to.getDate() - days);
    const key = (date: Date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
    api<{ items: CourseShareItem[]; totalMinutes: number }>(`/focus/summary?from=${key(from)}&to=${key(to)}`)
      .then(setShare)
      .catch(() => setShare({ items: [], totalMinutes: 0 }));
  }, [range]);

  // The donut answers ONE question: where did my focused time go in this range?
  // Minutes per course (range-scoped) — never mixed units like sessions-vs-tasks.
  const donut = useMemo(() => {
    const items = share?.items ?? [];
    const total = share?.totalMinutes || 1;
    let offset = 0;
    const slices = items.map((item, index) => {
      const fraction = item.minutes / Math.max(1, share?.totalMinutes || 1);
      const slice = { id: item.courseId || item.title, label: item.title || t("course.title"), value: item.minutes, fraction, offset, color: item.color || courseColor(item.courseId, index) };
      offset += fraction;
      return slice;
    });
    return { total: share?.totalMinutes ?? 0, slices };
  }, [share, t]);

  return (
    <div className="focus-analytics">
      <div className="focus-analytics__head">
        <div>
          <span className="section-kicker">{t("progress.activity")}</span>
          <h2>{t("focus.analyticsTitle")}</h2>
        </div>
        <div className="range-tabs" role="tablist">
          {(["week", "month", "year"] as const).map((option) => (
            <button key={option} role="tab" aria-selected={range === option} className={range === option ? "is-active" : ""} onClick={() => setRange(option)}>
              {t(`focus.range.${option}`)}
            </button>
          ))}
        </div>
      </div>

      <div className="focus-stats-row">
        <Card className="focus-stat">
          <span className="focus-stat__leaf">🍃</span>
          <div><small>{t("progress.studyTime")}</small><strong>{formatNumber(totalHours)} {t("progress.hours")}</strong></div>
        </Card>
        <Card className="focus-stat">
          <span className="focus-stat__leaf">⚡</span>
          <div><small>{t("focus.avgSession")}</small><strong>{formatNumber(avgMinutes)} {t("common.minutes")}</strong></div>
        </Card>
        <Card className="focus-stat">
          <span className="focus-stat__leaf">🌿</span>
          <div><small>{t("progress.masteredTopics")}</small><strong>{formatNumber(stats?.topics.mastered ?? 0)}</strong></div>
        </Card>
        <Card className="focus-stat">
          <span className="focus-stat__leaf">🗓️</span>
          <div><small>{t("focus.thisRange")}</small><strong>{formatNumber(buckets.length)}</strong></div>
        </Card>
      </div>

      <div className="focus-analytics__grid">
        <Card className="focus-chart-card tilt-card">
          <div className="focus-chart-card__head">
            <h3>{t("focus.focusTrend")}</h3>
            {buckets.length > 0 && trend?.pct !== null && trend?.pct !== undefined && (
              <span className="focus-bars__trend" role="status">
                {trend.pct > 0
                  ? t("focus.trendUp", { pct: formatNumber(trend.pct) })
                  : trend.pct < 0
                    ? t("focus.trendDown", { pct: formatNumber(Math.abs(trend.pct)) })
                    : t("focus.trendFlat")}
              </span>
            )}
          </div>
          {buckets.length && trend ? (
            <>
              <div className="focus-bars" role="img" aria-label={t("focus.focusTrend")}>
                {buckets.map((bucket, index) => (
                  <div className="focus-bars__col" key={`${bucket.label}-${index}`} style={{ "--delay": `${index * 50}ms` } as React.CSSProperties}>
                    <b className="focus-bars__value">{formatNumber(Math.round(bucket.seconds / 60))}</b>
                    <i
                      className={trend.bestIndex === index ? "is-best" : ""}
                      style={{ height: `${barHeightPercent(bucket.seconds, bucketMax)}%` }}
                      title={`${formatNumber(Math.round(bucket.seconds / 60))} ${t("common.minutes")}`}
                    />
                    <small>{bucket.label}</small>
                  </div>
                ))}
              </div>
              <p className="focus-bars__summary">
                {t("focus.trendTotal", { total: formatNumber(trend.total), avg: formatNumber(trend.avg) })}
                {trend.best > 0 && ` · ${t("focus.trendBest", { label: buckets[trend.bestIndex]?.label ?? "", best: formatNumber(trend.best) })}`}
              </p>
            </>
          ) : (
            <EmptyState icon="chart" title={t("progress.noFocusYet")} description={t("progress.startFocusHint")} />
          )}
        </Card>

        <Card className="focus-chart-card tilt-card">
          <h3>{t("focus.courseShare")}</h3>
          <p className="tree-picker__hint">{t("focus.courseShareHint")}</p>
          <div className="focus-donut">
            <div className="focus-donut__ring">
              {donut.slices.map((slice) => (
                <i key={slice.id} style={{
                  background: `conic-gradient(${slice.color} 0 ${(slice.fraction * 360).toFixed(1)}deg, transparent 0deg)`,
                  transform: `rotate(${(slice.offset * 360).toFixed(1)}deg)`
                }} aria-hidden="true" />
              ))}
              <strong>{formatNumber(donut.total)}</strong>
            </div>
            <ul className="focus-donut__legend">
              {donut.slices.map((slice) => (
                <li key={slice.id}>
                  <i style={{ background: slice.color }} />
                  {slice.label}
                  <b>{formatNumber(slice.value)} {t("common.minutes")} · {formatNumber(Math.round(slice.fraction * 100))}%</b>
                </li>
              ))}
              {!donut.slices.length && <li>{t("progress.noFocusYet")}</li>}
            </ul>
          </div>
        </Card>
      </div>

    </div>
  );
}
